import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, Clock, CheckCircle, Loader2, Video, Phone, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';
import { getStoredPartner, partnerHome } from '../lib/partnerContext';
import { toast } from 'sonner';
import RgpdCheckbox from '../components/RgpdCheckbox';

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS_FR = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export default function ConseilRdvPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const partnerHomePath = partnerHome(getStoredPartner());
  const conseilId = searchParams.get('id');
  const email = searchParams.get('email');
  const code = searchParams.get('code');

  const [conseil, setConseil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [contactMethod, setContactMethod] = useState('phone');
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(false);
  const [rgpdChecked, setRgpdChecked] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!conseilId || !email) {
        setError('Lien invalide');
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/api/conseil/suivi/${conseilId}?email=${encodeURIComponent(email)}`);
        setConseil(res.data);
        if (res.data?.rdv_date) {
          setBooked(true);
        }
      } catch (e) {
        setError(e.response?.data?.detail || 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [conseilId, email]);

  const fetchSlots = async (dateStr) => {
    setLoadingSlots(true);
    setSlots([]);
    setSelectedSlot(null);
    try {
      const res = await axios.get(`${API}/api/calendar/available-slots?date=${dateStr}`);
      if (!res.data.available) {
        toast.info(res.data.reason || 'Aucun créneau disponible ce jour');
        setSlots([]);
      } else {
        setSlots(res.data.slots || []);
      }
    } catch (e) {
      toast.error('Erreur lors du chargement des créneaux');
    } finally {
      setLoadingSlots(false);
    }
  };

  const pickDate = (date) => {
    const dateStr = formatDate(date);
    setSelectedDate(dateStr);
    fetchSlots(dateStr);
  };

  const confirmRdv = async () => {
    if (!selectedDate || !selectedSlot) {
      toast.error('Sélectionnez une date et un créneau');
      return;
    }
    setBooking(true);
    try {
      await axios.post(`${API}/api/conseil/book-rdv`, {
        conseil_id: conseilId,
        email,
        date: selectedDate,
        time: selectedSlot,
        contact_method: contactMethod
      });
      toast.success('RDV confirmé !');
      setBooked(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la réservation');
    } finally {
      setBooking(false);
    }
  };

  // Generate next 14 days excluding past
  const generateDays = () => {
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i < 21; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      days.push(d);
    }
    return days;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-navy to-slate-900 flex items-center justify-center p-4">
        <Loader2 className="w-12 h-12 text-gold animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-navy to-slate-900 flex items-center justify-center p-4">
        <Card className="bg-white max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <h2 className="text-xl font-bold text-navy mb-2">Oups !</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate(partnerHomePath)} data-testid="back-home-btn">Retour à l'accueil</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (booked && conseil?.rdv_date) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-navy to-slate-900 flex items-center justify-center p-4">
        <Card className="bg-white max-w-lg w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100 mb-4">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-navy mb-3">RDV Confirmé !</h1>
            <div className="bg-gold/10 border-2 border-gold/40 rounded-xl p-5 mb-4 text-left">
              <p className="mb-2"><strong>Date :</strong> {conseil.rdv_date}</p>
              <p className="mb-2"><strong>Heure :</strong> {conseil.rdv_time}</p>
              <p className="mb-2"><strong>Mode :</strong> {conseil.rdv_contact_method === 'visio' ? 'Visioconférence' : 'Téléphone'}</p>
              <p><strong>Code dossier :</strong> <span className="font-mono">{conseil.tracking_code}</span></p>
            </div>
            <p className="text-muted-foreground text-sm mb-6">Un email de confirmation vient de vous être envoyé.</p>
            <Button
              data-testid="go-to-dossier-btn"
              onClick={() => navigate(`/conseil/suivi?id=${conseilId}&email=${encodeURIComponent(email)}&code=${code || ''}`)}
              className="bg-navy hover:bg-navy/90 text-white"
            >
              Accéder à mon dossier
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const days = generateDays();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-navy to-slate-900 p-4 md:p-8" data-testid="conseil-rdv-page">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(`/conseil/suivi?id=${conseilId}&email=${encodeURIComponent(email)}&code=${code || ''}`)}
          className="text-white mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour au dossier
        </Button>

        <Card className="bg-white shadow-2xl">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-gold" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-navy">Réservez votre créneau</h1>
                <p className="text-sm text-muted-foreground">
                  Dossier <span className="font-mono text-navy">{conseil?.tracking_code}</span> · {conseil?.conseil_type_label}
                </p>
              </div>
            </div>

            {/* Date selection */}
            <div className="mb-6">
              <h3 className="font-semibold text-navy mb-3">1. Choisissez une date</h3>
              <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
                {days.map((d) => {
                  const dateStr = formatDate(d);
                  const isSelected = selectedDate === dateStr;
                  return (
                    <button
                      key={dateStr}
                      onClick={() => pickDate(d)}
                      data-testid={`date-option-${dateStr}`}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        isSelected
                          ? 'border-gold bg-gold/10 text-navy'
                          : 'border-gray-200 hover:border-gold/40 hover:bg-gold/5'
                      }`}
                    >
                      <p className="text-xs text-muted-foreground">{DAYS_FR[d.getDay()].slice(0, 3)}</p>
                      <p className="text-lg font-bold text-navy">{d.getDate()}</p>
                      <p className="text-xs text-muted-foreground">{MONTHS_FR[d.getMonth()].slice(0, 3)}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Slot selection */}
            {selectedDate && (
              <div className="mb-6">
                <h3 className="font-semibold text-navy mb-3">2. Choisissez un créneau</h3>
                {loadingSlots ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 text-gold animate-spin" />
                  </div>
                ) : slots.length === 0 ? (
                  <p className="text-muted-foreground text-sm py-4">Aucun créneau disponible ce jour. Choisissez une autre date.</p>
                ) : (
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                    {slots.map((slot) => (
                      <button
                        key={slot}
                        onClick={() => setSelectedSlot(slot)}
                        data-testid={`slot-option-${slot}`}
                        className={`p-3 rounded-lg border-2 text-center transition-all flex items-center justify-center gap-1 ${
                          selectedSlot === slot
                            ? 'border-gold bg-gold/10 text-navy font-semibold'
                            : 'border-gray-200 hover:border-gold/40'
                        }`}
                      >
                        <Clock className="w-3 h-3" /> {slot}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Contact method */}
            {selectedSlot && (
              <div className="mb-6">
                <h3 className="font-semibold text-navy mb-3">3. Mode de consultation</h3>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setContactMethod('phone')}
                    data-testid="method-phone-btn"
                    className={`p-4 rounded-lg border-2 flex items-center gap-3 transition-all ${
                      contactMethod === 'phone' ? 'border-gold bg-gold/10' : 'border-gray-200 hover:border-gold/40'
                    }`}
                  >
                    <Phone className="w-5 h-5 text-navy" />
                    <span className="font-medium text-navy">Téléphone</span>
                  </button>
                  <button
                    onClick={() => setContactMethod('visio')}
                    data-testid="method-visio-btn"
                    className={`p-4 rounded-lg border-2 flex items-center gap-3 transition-all ${
                      contactMethod === 'visio' ? 'border-gold bg-gold/10' : 'border-gray-200 hover:border-gold/40'
                    }`}
                  >
                    <Video className="w-5 h-5 text-navy" />
                    <span className="font-medium text-navy">Visio</span>
                  </button>
                </div>
              </div>
            )}

            {/* Confirm button */}
            {selectedDate && selectedSlot && (
              <>
                <RgpdCheckbox checked={rgpdChecked} onChange={setRgpdChecked} companyName={conseil?.partner_company_name || conseil?.partner_name || null} />
                <Button
                  onClick={confirmRdv}
                  disabled={booking || !rgpdChecked}
                  data-testid="confirm-rdv-btn"
                  className="w-full bg-gold hover:bg-gold/90 text-black font-semibold py-6 text-lg mt-2"
                >
                {booking ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Réservation...</>
                ) : (
                  <>Confirmer le RDV du {selectedDate} à {selectedSlot}</>
                )}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
